import {
  createContext,
  useContext,
  useState,
  type ComponentProps,
  type ReactNode,
} from "react";
import { EllipsisIcon } from "lucide-react";

import { Popper } from "@/components/atoms/Popper";
import { useDevice } from "@/hooks/useDevice";
import { Button } from "@/components/ui/button";

/**
 * The "⋯" menu's column: what it holds one above another, every button in it
 * a finger's height with its words at its start.
 */
export const APP_VIEW_SUBNAV_MENU_CLASS =
  "flex flex-col items-stretch gap-1 p-1 min-w-55 [&_[data-slot=button]]:justify-start [&_[data-slot=button]]:min-h-11";

/** Whether a subnav action is drawn in the "⋯" menu rather than the row. */
export const AppViewSubnavMenuContext = createContext(false);

/**
 * One thing done to a list from its subnav. In the row it is its words, as
 * ever; in the "⋯" menu, where there is the room, its icon and its words.
 */
export function AppViewSubnavAction({
  icon,
  children,
  ...props
}: ComponentProps<typeof Button> & { icon: ReactNode }) {
  const isInMenu = useContext(AppViewSubnavMenuContext);
  return (
    <Button variant="outline" size="sm" {...props}>
      {isInMenu ? icon : undefined}
      {children}
    </Button>
  );
}

type AppViewSubnavProps = {
  leading?: React.ReactNode;
  filters?: React.ReactNode;
  nav?: React.ReactNode;
  actions?: React.ReactNode;
};

export function AppViewSubnav({
  leading,
  filters,
  nav,
  actions,
}: AppViewSubnavProps) {
  const hasTrailing = Boolean(actions);
  // On a phone - or in a window as narrow - the row is the list's own
  // controls - its mode, its sort and filters - and what is done to the list
  // (add, import, export) waits behind one button at its end, a tap away
  // rather than crowding the filters off.
  const { isNarrow } = useDevice();
  const [isMoreOpen, setIsMoreOpen] = useState(false);

  if (!leading && !filters && !nav && !hasTrailing) {
    return null;
  }

  return (
    <div className="min-w-0 justify-start max-w-full grow flex flex-nowrap pl-2 pr-2 items-center gap-2 w-full h-full min-h-12 overflow-visible">
      {leading}
      {filters && (
        <div className="flex grow items-center min-w-0 min-h-12 h-full overflow-visible">
          {filters}
        </div>
      )}
      {isNarrow && (nav || hasTrailing) ? (
        <>
          {!filters && <div className="grow" />}
          <Popper
            isOpenState={[isMoreOpen, setIsMoreOpen]}
            isClickable={false}
            placement="bottom-end"
            anchor={
              <Button
                variant="ghost"
                size="icon-sm"
                aria-label="More actions"
                data-testid="app-view-subnav-more"
                onClick={() => setIsMoreOpen(!isMoreOpen)}
                className="shrink-0"
              >
                <EllipsisIcon />
              </Button>
            }
          >
            <AppViewSubnavMenuContext.Provider value={true}>
              <div
                data-testid="app-view-subnav-more-actions"
                // An action taken, the menu goes.
                onClick={() => setIsMoreOpen(false)}
                className={APP_VIEW_SUBNAV_MENU_CLASS}
              >
                {nav}
                {actions}
              </div>
            </AppViewSubnavMenuContext.Provider>
          </Popper>
        </>
      ) : null}
      {!isNarrow && nav && (
        <div className="flex items-center min-w-0">{nav}</div>
      )}
      {!isNarrow && hasTrailing && (
        <>
          {!filters && <div className="grow" />}
          {/* Its end on the top bar's: the row's own room and no more. */}
          <div className="flex flex-row gap-2 items-center min-w-fit shrink-0">
            {actions}
          </div>
        </>
      )}
    </div>
  );
}

/** Full-width filter strip (e.g. reports dashboard row 3). */
export function AppViewFilterRow({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex items-center w-full h-full min-w-0 min-h-0 pl-2 pr-2 pt-0 pb-0 overflow-x-auto overflow-y-hidden [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden">
      {children}
    </div>
  );
}
